import React, {forwardRef, useCallback, useMemo} from 'react';
import CircularLoader from "./CircularLoader";
import {Spin} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
// think about changing it over to an AntD button which handles more built in features of buttons
// import { Button as AntDButton } from 'antd';
export const BUTTON_SIZE = {
    SMALL:'small',
    MEDIUM:'medium'
}

export const BUTTON_TYPE = {
    PRIMARY:'primary',
    TEXT:'text',
    TEXT_UNDERLINE:'text_underline',
    OUTLINE:'outline',
    DASHED:'dashed',
    SELECTOR:'selector'
}

export const BUTTON_ICON_POSITION = {
    LEFT: 'left',
    RIGHT: 'right'
}

const BUTTON_SIZE_CLASS = {
    [BUTTON_SIZE.SMALL]:'ch-btn-small',
    [BUTTON_SIZE.MEDIUM]:'ch-btn-medium'
}

const BUTTON_TYPE_CLASS = {
    [BUTTON_TYPE.PRIMARY]: 'ch-btn-primary',
    [BUTTON_TYPE.TEXT]: 'ch-btn-text',
    [BUTTON_TYPE.OUTLINE]: 'ch-btn-outline',
    [BUTTON_TYPE.DASHED]: 'ch-btn-dashed',
    [BUTTON_TYPE.TEXT_UNDERLINE]: 'ch-btn-text ch-btn-text-underline',
    [BUTTON_TYPE.SELECTOR]: 'ch-btn-selector'
}

/**
 * @typedef {object} ButtonProps
 * @property {'primary'|'text'|'text_underline'|'outline'|'dashed'|'selector'} [type]
 * @property {string} [className]
 * @property {'small'|'medium'} [size] - DO NOT USE 'small'. Always use default (medium).
 * @property {React.ReactNode} [icon]
 * @property {'left'|'right'} [iconPosition]
 * @property {boolean} [disabled]
 * @property {boolean} [activated]
 * @property {boolean} [loading]
 * @property {(event: React.MouseEvent<HTMLButtonElement>) => void} [onClick]
 * @property {(event: React.FocusEvent<HTMLButtonElement>) => void} [onFocus]
 * @property {(event: React.FocusEvent<HTMLButtonElement>) => void} [onBlur]
 * @property {React.ReactNode} [children]
 * @property {'button'|'submit'|'reset'} [htmlType]
 */

/** @type {React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLButtonElement>>} */
const Button = forwardRef((props, ref) => {
    const {type, className, size, icon, iconPosition = BUTTON_ICON_POSITION.LEFT, disabled, activated, loading = false, onClick, onFocus, onBlur, children, htmlType, ...other} = props;
    const extraProps = {};

    const hasChildren = React.Children.count(children) > 0;

    //added in for accessibility
    const cloneIcon = useMemo(()=>{
        if (!icon || !React.isValidElement(icon)) return null;
        return React.cloneElement(icon, {"aria-hidden":true});
    },[icon]);

    if (disabled || loading) {
        extraProps.disabled = 'disabled';
        extraProps['aria-disabled'] = 'true';
    }

    if (activated != undefined) {
        extraProps["aria-pressed"] = activated;
    }

    const handleClick = (event) => {
        if (loading || disabled) {
            event.preventDefault();
            return;
        }
        if (onClick) {
            onClick(event);
        }
    };

    const getLoader = useCallback(() => {
        return loading ? <Spin style={{paddingLeft:(iconPosition === BUTTON_ICON_POSITION.RIGHT ?'6px':'4px'),
        paddingRight:(iconPosition === BUTTON_ICON_POSITION.LEFT ?'6px':'4px')}} size="small" /> : cloneIcon;
    }, [loading]);

    return (
        <button
            ref={ref}
            className={'ch-button ch-btn' +
                (type ? ' '+BUTTON_TYPE_CLASS[type]:'') +
                (size ? ' '+BUTTON_SIZE_CLASS[size] : '') +
                (cloneIcon && hasChildren ? ' ch-button-icon-' + (iconPosition === BUTTON_ICON_POSITION.LEFT ? 'l' : 'r') : '') +
                (activated ? ' ch-activated' : '') +
                (disabled ? ' ch-button-disabled' : '') +
                (className ? ' ' + className : '')}
            {...extraProps}
            {...other}
            type={disabled || loading ? "button" : htmlType}
            onClick={handleClick}
            onBlur={onBlur}
            onFocus={onFocus}
        >
            <>
                {cloneIcon && iconPosition === BUTTON_ICON_POSITION.LEFT && (
                    <>{loading ? getLoader() : cloneIcon}</>
                )}
                {children}
                {cloneIcon && iconPosition === BUTTON_ICON_POSITION.RIGHT && (
                    <>{loading ? getLoader() : cloneIcon}</>
                )}
            </>
        </button>
    );
});

export default Button;